import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { useInventoryRealtime } from "@/hooks/use-inventory-realtime";
import { getStateBalances, transitionHold, qcDecision } from "@/lib/inventory-engine.functions";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/inventory/holds")({ component: HoldsPage });

function HoldsPage() {
  const { tenantId, facilityId, customerId } = useScope();
  const qc = useQueryClient();
  const balFn = useServerFn(getStateBalances);
  const releaseFn = useServerFn(transitionHold);
  const qcFn = useServerFn(qcDecision);

  const { data } = useQuery({
    queryKey: ["holds-balances", tenantId, facilityId, customerId],
    enabled: !!tenantId,
    queryFn: () => balFn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null, customerId: customerId ?? null, skuId: null, groupBy: "none" } }),
    placeholderData: keepPreviousData,
  });
  useInventoryRealtime(tenantId, [["holds-balances", tenantId, facilityId, customerId]]);

  const release = useMutation({
    mutationFn: (r: any) => releaseFn({ data: { action: "release",
      tenantId: tenantId!, customerId: r.customer_id, facilityId: r.facility_id,
      skuId: r.sku_id, locationId: r.location_id, lot: r.lot ?? "", qty: r.qty } }),
    onSuccess: () => { toast.success("Hold released"); qc.invalidateQueries({ queryKey: ["holds-balances"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const qcAct = useMutation({
    mutationFn: (v: { r: any; decision: "pass" | "fail" }) => qcFn({ data: {
      decision: v.decision, tenantId: tenantId!, customerId: v.r.customer_id,
      facilityId: v.r.facility_id, skuId: v.r.sku_id, locationId: v.r.location_id,
      lot: v.r.lot ?? "", qty: v.r.qty, reason: v.decision === "fail" ? "qc_reject" : undefined } }),
    onSuccess: () => { toast.success("QC decision recorded"); qc.invalidateQueries({ queryKey: ["holds-balances"] }); },
    onError: (e: any) => toast.error(e.message),
  });

  const rows = ((data?.rows ?? []) as any[]).filter((r) => r.state === "hold" || r.state === "awaiting_qc");

  return (
    <div className="p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Holds & Quarantine</h2>
        <p className="text-sm text-muted-foreground">Inventory currently on hold or awaiting QC. Release or rule on each line.</p>
      </div>
      <div className="border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr><th className="text-left px-3 py-2">State</th><th className="text-left px-3 py-2">SKU</th><th className="text-left px-3 py-2">Loc</th><th className="text-left px-3 py-2">Lot</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2 w-72">Action</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t">
                <td className="px-3 py-2 capitalize">{r.state.replace("_", " ")}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.sku_id?.slice(0, 8)}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.location_id?.slice(0, 8)}</td>
                <td className="px-3 py-2 text-xs">{r.lot || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.qty}</td>
                <td className="px-3 py-2 text-right space-x-2">
                  {r.state === "hold" && (
                    <Button size="sm" variant="outline" disabled={release.isPending} onClick={() => release.mutate(r)}>Release</Button>
                  )}
                  {r.state === "awaiting_qc" && (<>
                    <Button size="sm" variant="outline" disabled={qcAct.isPending} onClick={() => qcAct.mutate({ r, decision: "pass" })}>Pass</Button>
                    <Button size="sm" variant="destructive" disabled={qcAct.isPending} onClick={() => qcAct.mutate({ r, decision: "fail" })}>Fail</Button>
                  </>)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-8">Nothing on hold or awaiting QC.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
